import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Styles from './softwareDeploymentStyle.module.scss';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useSoftwareDeployment } from '../../components/services/softwareDeploymentService';
import type { UploadProgress } from '../../components/services/softwareDeploymentService';

type PatchDeploymentFormValues = {
  appName: string;
  publisher: string;
  osType: string;
  osVersion: string;
  detection: string;
  installationParameters: string;
  preScript: string;
  installScript: string;
  postScript: string;
  file: File | null;
};

const PatchDeploymentForm: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const { addSoftwareDeployment } = useSoftwareDeployment();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  const formik = useFormik<PatchDeploymentFormValues>({
    initialValues: {
      appName: '',
      publisher: '',
      osType: '',
      osVersion: '',
      detection: '',
      installationParameters: '',
      preScript: '',
      installScript: '',
      postScript: '',
      file: null,
    },
    validationSchema: Yup.object({
      appName: Yup.string().required('Required'),
      publisher: Yup.string().required('Required'),
      osType: Yup.string().required('Required'),
      detection: Yup.string().required('Required'),
      file: Yup.mixed().required('A file is required'),
      installationParameters: Yup.string(),
    }),
    onSubmit: (values) => handleSoftwareDeployment(values),
  });

  const handleSoftwareDeployment = async (values: PatchDeploymentFormValues) => {
    setLoading(true);
    setUploadProgress(null);
    try {
      if (!values.file) {
        addToast('Please choose a file before submitting.', 'error');
        setLoading(false);
        return;
      }

      if (!auth?.userData) throw new Error('User not authenticated');
      const token = auth.userData.access_token;

      await addSoftwareDeployment(
        selectedTenantId as string,
        token,
        {
          file: values.file,
          appName: values.appName,
          publisher: values.publisher,
          osType: values.osType,
          detection: values.detection,
          installationParameters: values.installationParameters,
          preScript: values.preScript,
          installScript: values.installScript,
          postScript: values.postScript,
        },
        {
          onProgress: (progress) => setUploadProgress(progress),
        },
      );

      addToast('Deployment completed successfully!', 'success', 4000);
      await new Promise((resolve) => setTimeout(resolve, 3000));
      navigate('/SoftwareDeployment');
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} className="bg-1D1D1D text-white p-6 rounded space-y-6 shadow-md">
      <h2 className="text-xl mb-4">Patch Deployment Setup</h2>

      {/* Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label>Application Name *</label>
          <input
            name="appName"
            type="text"
            className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
            style={{ backgroundColor: '#161617' }}
            onChange={formik.handleChange}
            value={formik.values.appName}
          />
          {formik.touched.appName && formik.errors.appName && <div className="text-red-500 text-sm">{formik.errors.appName}</div>}
        </div>

        <div>
          <label>Publisher *</label>
          <input
            name="publisher"
            type="text"
            className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
            style={{ backgroundColor: '#161617' }}
            onChange={formik.handleChange}
            value={formik.values.publisher}
          />
          {formik.touched.publisher && formik.errors.publisher && <div className="text-red-500 text-sm">{formik.errors.publisher}</div>}
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label>Operating System Type *</label>
          <select
            name="osType"
            className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
            style={{ backgroundColor: '#161617' }}
            onChange={formik.handleChange}
            value={formik.values.osType}
          >
            <option value="">Select</option>
            <option value="Windows">Windows</option>
          </select>
          {formik.touched.osType && formik.errors.osType && <div className="text-red-500 text-sm">{formik.errors.osType}</div>}
        </div>

        <div>
          <label>Architecture*</label>
          <select
            name="detection"
            className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
            style={{ backgroundColor: '#161617' }}
            onChange={formik.handleChange}
            value={formik.values.detection}
          >
            <option value="">Select</option>
            <option value="x86">x86</option>
            <option value="x64">x64</option>
            <option value="Both">Both (x86 and x64)</option>
          </select>
          {formik.touched.detection && formik.errors.detection && <div className="text-red-500 text-sm">{formik.errors.detection}</div>}
        </div>
      </div>

      {/* Installation Parameters - Single Textbox */}
      <div>
        <label>Installation Parameters</label>
        <textarea
          name="installationParameters"
          className="w-full border border-gray-700 px-3 py-2 rounded mt-1 font-mono"
          style={{ backgroundColor: '#161617' }}
          placeholder="Enter switches and arguments (e.g., /S /D=C:\Program Files\App KEY=VALUE)"
          rows={4}
          onChange={formik.handleChange}
          value={formik.values.installationParameters}
        />
        <div className="text-gray-400 text-xs mt-1">
          Optional: Enter all installation switches, arguments, and key-value pairs. Can include special characters.
        </div>
      </div>

      {/* Scripts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label>Pre-install Script</label>
          <textarea
            name="preScript"
            className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
            style={{ backgroundColor: '#161617' }}
            onChange={formik.handleChange}
            value={formik.values.preScript}
          />
        </div>

        <div>
          <label>Install Script</label>
          <textarea
            name="installScript"
            className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
            style={{ backgroundColor: '#161617' }}
            onChange={formik.handleChange}
            value={formik.values.installScript}
          />
        </div>
      </div>

      <div>
        <label>Post-install Script</label>
        <textarea
          name="postScript"
          className="w-full border border-gray-700 px-3 py-2 rounded mt-1"
          style={{ backgroundColor: '#161617' }}
          onChange={formik.handleChange}
          value={formik.values.postScript}
        />
      </div>

      {/* File Upload */}
      <div>
        <label>Add File *</label>
        <div className="border border-gray-700 rounded p-6 text-center">
          <input
            id="file"
            name="file"
            type="file"
            accept=".exe,.msi,.msix"
            onChange={(event) => formik.setFieldValue('file', event.currentTarget.files?.[0] || null)}
            className="hidden"
            style={{ backgroundColor: '#161617' }}
          />
          <label htmlFor="file" className="cursor-pointer text-red-500 underline">
            {formik.values.file ? formik.values.file.name : 'Browse Files'}
          </label>
          {formik.touched.file && formik.errors.file && <div className="text-red-500 text-sm mt-2">{formik.errors.file}</div>}
        </div>
      </div>

      {uploadProgress && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-300">
            <span>Upload Progress</span>
            <span>{uploadProgress.percentage}%</span>
          </div>
          <div className="w-full h-3 bg-gray-700 rounded">
            <div
              className="h-3 bg-red-500 rounded"
              style={{ width: `${uploadProgress.percentage}%` }}
              aria-valuenow={uploadProgress.percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          {uploadProgress.estimatedTimeRemaining !== undefined && (
            <div className="text-xs text-gray-400">~{Math.max(1, Math.round(uploadProgress.estimatedTimeRemaining))}s remaining</div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="flex space-x-4">
        <button type="button" className={Styles['ClearBtn']} onClick={() => navigate('/SoftwareDeployment')}>
          Discard
        </button>
        <button type="submit" className={`${Styles['submitBtn']} flex items-center justify-center space-x-2`} disabled={loading}>
          <span>{loading ? 'Submitting...' : 'Submit'}</span>
        </button>
      </div>
    </form>
  );
};

export default PatchDeploymentForm;
